import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { api, FILE_URL } from "@/lib/api";
import IdeaCard from "@/components/IdeaCard";
import { FollowListModal } from "@/components/FollowListModal";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  UserPlus,
  UserMinus,
  Camera,
  RotateCcw,
  Sprout,
  Gift,
  Pencil,
  Check,
  X as XIcon,
  Image as ImageIcon,
  Eye,
  EyeOff,
  MapPin,
  Sparkles,
  Bookmark,
  Star,
  Move,
  Instagram,
  Facebook,
} from "lucide-react";
import BannerAdjuster from "@/components/BannerAdjuster";

const TAGLINE_MAX = 80;
const BANNER_FOCUS_POS = { top: "0%", center: "50%", bottom: "100%" };

// --- Social handle helpers ---
// Instagram is stored as a bare handle server-side, so a full URL is a
// simple prepend. Facebook is either a bare handle OR a full URL because
// FB uses share slugs, vanity URLs, and pages interchangeably — so we
// treat anything starting with "http" as a URL, else prepend facebook.com.
export const instagramUrl = (handle) => handle ? `https://instagram.com/${handle}` : null;
export const facebookUrl = (value) => {
  if (!value) return null;
  return /^https?:\/\//i.test(value) ? value : `https://facebook.com/${value}`;
};

export default function Roots() {
  const { user_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [regrows, setRegrows] = useState([]);
  const [regrowsLoading, setRegrowsLoading] = useState(false);
  const [savedBranches, setSavedBranches] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [streakWeeks, setStreakWeeks] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeThisMonth, setActiveThisMonth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);
  // Only the profile owner sees the reposition-and-zoom panel, and only when
  // they explicitly open it. Auto-closes on save.
  const [bannerAdjusting, setBannerAdjusting] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [form, setForm] = useState({ location: "", location_public: false, tagline: "", banner_focus: "center", instagram_handle: "", facebook_url: "" });
  const [savingForm, setSavingForm] = useState(false);
  // "followers" | "following" | null. Only the profile owner can open these.
  const [followListMode, setFollowListMode] = useState(null);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(`/users/${user_id}`).then(({ data }) => {
      setProfile(data.user);
      setIdeas(data.ideas);
      setFollowerCount(data.follower_count || 0);
      setFollowingCount(data.following_count || 0);
      setStreakWeeks(data.streak_weeks || 0);
      setIsFollowing(!!data.is_following);
      setActiveThisMonth(!!data.active_this_month);
      setForm({
        location: data.user.location || "",
        location_public: !!data.user.location_public,
        tagline: data.user.tagline || "",
        banner_focus: data.user.banner_focus || "center",
        instagram_handle: data.user.instagram_handle || "",
        facebook_url: data.user.facebook_url || "",
      });
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user_id, user?.user_id]);

  // Load Regrows this user has done — chronological, newest first. Rendered as
  // a separate section below Seeds. Regrows never bump the "Seeds" total (that
  // stat only counts original seeds).
  useEffect(() => {
    if (!user_id) return;
    setRegrowsLoading(true);
    api.get(`/users/${user_id}/regrows`)
      .then(({ data }) => setRegrows(Array.isArray(data) ? data : []))
      .catch(() => setRegrows([]))
      .finally(() => setRegrowsLoading(false));
  }, [user_id]);

  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    api.get("/saves/ids").then(({ data }) => setSavedIds(new Set(data.ids || []))).catch(() => {});
  }, [user]);

  const onSaveChange = (id, next) => {
    setSavedIds((prev) => {
      const s = new Set(prev);
      if (next) s.add(id); else s.delete(id);
      return s;
    });
  };

  const isMe = user && profile && user.user_id === profile.user_id;
  // Tab is owner-only. `?tab=saved` opens the Saved shelf inline on the
  // owner's own Roots (Instagram-style — saved posts live under your profile).
  // Any non-owner viewer ignores the query param and always sees branches.
  const activeTab = isMe && searchParams.get("tab") === "saved" ? "saved" : "branches";

  // Lazy-fetch saved branches only when the Saved tab is entered.
  useEffect(() => {
    if (!isMe || activeTab !== "saved") return;
    setSavedLoading(true);
    api.get("/your-grove").then(({ data }) => {
      setSavedBranches(Array.isArray(data) ? data : []);
    }).catch(() => setSavedBranches([]))
      .finally(() => setSavedLoading(false));
  }, [isMe, activeTab]);

  const goToTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "branches") next.delete("tab"); else next.set("tab", tab);
    setSearchParams(next, { replace: false });
  };

  const saveProfile = async () => {
    setSavingForm(true);
    try {
      const { data } = await api.patch("/users/me", {
        location: form.location.trim() || "",
        location_public: form.location_public,
        tagline: form.tagline.trim() || "",
        banner_focus: form.banner_focus,
        instagram_handle: form.instagram_handle.trim(),
        facebook_url: form.facebook_url.trim(),
      });
      setUser(data);
      setProfile((prev) => ({ ...prev, ...data }));
      // Sync the form with server-normalised values so reopening the drawer
      // (before a page reload) shows the clean handle rather than the raw
      // '@name' / 'https://…' the user pasted.
      setForm((f) => ({
        ...f,
        location: data.location || "",
        location_public: !!data.location_public,
        tagline: data.tagline || "",
        banner_focus: data.banner_focus || f.banner_focus,
        instagram_handle: data.instagram_handle || "",
        facebook_url: data.facebook_url || "",
      }));
      setEditingProfile(false);
      toast("Profile updated.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not update profile.");
    } finally {
      setSavingForm(false);
    }
  };

  const toggleFollow = async () => {
    if (!user) return toast("Sign in to follow a sharer.");
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await api.delete(`/users/${user_id}/follow`);
        setIsFollowing(false);
        setFollowerCount((c) => Math.max(0, c - 1));
        toast("Unfollowed.");
      } else {
        await api.post(`/users/${user_id}/follow`);
        setIsFollowing(true);
        setFollowerCount((c) => c + 1);
        toast(`Following ${profile.name}.`);
      }
    } catch {
      toast("Could not update follow.");
    } finally {
      setFollowBusy(false);
    }
  };

  const onPickAvatar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast("Please choose an image.");
    if (f.size > 5 * 1024 * 1024) return toast("Please keep it under 5MB.");
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: me } = await api.patch("/users/me", { custom_picture_path: up.path });
      setUser(me);
      setProfile((prev) => ({ ...prev, ...me }));
      toast("Profile photo updated.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not update photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const resetAvatar = async () => {
    setAvatarBusy(true);
    try {
      const { data: me } = await api.patch("/users/me", { custom_picture_path: "" });
      setUser(me);
      setProfile((prev) => ({ ...prev, ...me, custom_picture_path: null }));
      toast("Back to your Google photo.");
    } catch {
      toast("Could not reset photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onPickBanner = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast("Please choose an image.");
    if (f.size > 8 * 1024 * 1024) return toast("Please keep the banner under 8MB.");
    setBannerBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: me } = await api.patch("/users/me", { banner_photo_path: up.path });
      setUser(me);
      setProfile((prev) => ({ ...prev, ...me }));
      toast("Banner updated.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not update banner.");
    } finally {
      setBannerBusy(false);
    }
  };

  const removeBanner = async () => {
    setBannerBusy(true);
    try {
      const { data: me } = await api.patch("/users/me", { banner_photo_path: "" });
      setUser(me);
      setProfile((prev) => ({ ...prev, ...me, banner_photo_path: null }));
      toast("Banner removed.");
    } catch {
      toast("Could not remove banner.");
    } finally {
      setBannerBusy(false);
    }
  };

  // Save the new pan+zoom values from BannerAdjuster. The adjuster panel
  // is presentational — it hands us the final { banner_offset_x, y, scale }
  // and we push them to /users/me. On success we update local state so the
  // hero banner reflects the new framing immediately.
  const saveBannerAdjust = async ({ banner_offset_x, banner_offset_y, banner_scale }) => {
    setBannerBusy(true);
    try {
      const { data: me } = await api.patch("/users/me", {
        banner_offset_x,
        banner_offset_y,
        banner_scale,
      });
      setUser(me);
      setProfile((prev) => ({ ...prev, ...me }));
      setBannerAdjusting(false);
      toast("Banner position saved.");
    } catch {
      toast("Could not save banner position.");
    } finally {
      setBannerBusy(false);
    }
  };

  // Backend already sorts newest-first, but keep a client-side sort so any
  // future rehydration path can't accidentally reorder them.
  const sortedIdeas = useMemo(
    () =>
      [...ideas].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [ideas],
  );

  if (loading || !profile) return <div className="max-w-6xl mx-auto px-6 pt-20 text-[#2A2823]/60">Loading…</div>;

  const avatarSrc = profile.custom_picture_path
    ? FILE_URL(profile.custom_picture_path)
    : profile.picture;
  const DEFAULT_BANNER = "/img/default-banner.png";
  // Custom banner beats the Willoah-branded default. When someone hasn't
  // uploaded one, the default gives their profile visual presence without
  // looking abandoned — same skinny aspect ratio either way. Focus toggle
  // still applies (top/center/bottom) even to the default in case a future
  // banner has visible text near an edge.
  const bannerSrc = profile.banner_photo_path
    ? FILE_URL(profile.banner_photo_path)
    : DEFAULT_BANNER;
  const isDefaultBanner = !profile.banner_photo_path;
  const bannerFocus = BANNER_FOCUS_POS[profile.banner_focus] || "50%";
  // Pan+zoom overrides. If either offset is set on the profile we use the new
  // pan/zoom model; otherwise fall back to the legacy focus toggle so old
  // profiles keep rendering exactly as they used to.
  const hasPanZoom =
    profile.banner_offset_x !== undefined ||
    profile.banner_offset_y !== undefined ||
    profile.banner_scale !== undefined;
  const bannerOffsetX = profile.banner_offset_x ?? 50;
  const bannerOffsetY = profile.banner_offset_y ?? 50;
  const bannerScale = profile.banner_scale ?? 1;
  const bannerImgStyle = hasPanZoom
    ? {
        objectPosition: `${bannerOffsetX}% ${bannerOffsetY}%`,
        transform: `scale(${bannerScale})`,
        transformOrigin: `${bannerOffsetX}% ${bannerOffsetY}%`,
      }
    : { objectPosition: `50% ${bannerFocus}` };

  return (
    <>
    <main className="max-w-6xl mx-auto px-3 sm:px-6 pt-6 pb-24 overflow-x-hidden">
      {/* Banner. Uses `object-cover` for the default (so the branded art
          fills the frame edge-to-edge no matter the screen width) and
          switches to `object-contain` for user-uploaded banners on mobile —
          text banners like "planted by the water" would otherwise get
          horizontally cropped on narrow phones. Above sm, both variants
          fill the frame. Container itself stays skinny (aspect-[3/1] on
          mobile, [6/1] on desktop) — deliberately narrower than a
          Facebook-style cover. */}
      <div
        data-testid="roots-banner"
        className="relative w-full aspect-[3/1] sm:aspect-[3.2/1] min-h-[140px] rounded-3xl overflow-hidden border border-[#B79256]/25 bg-[#F0E6D0]"
      >
        <img
          src={bannerSrc}
          alt={isDefaultBanner ? "Willoah default banner" : `${profile.name}'s banner`}
          data-testid={isDefaultBanner ? "roots-banner-img-default" : "roots-banner-img"}
          className={`absolute inset-0 w-full h-full ${isDefaultBanner ? "object-contain sm:object-cover object-center" : "object-contain sm:object-cover"}`}
          style={bannerImgStyle}
          referrerPolicy="no-referrer"
        />
        {isMe && (
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button
              type="button"
              data-testid="change-banner-btn"
              onClick={() => bannerInputRef.current?.click()}
              disabled={bannerBusy}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-[#FBF8F1]/85 hover:bg-[#FBF8F1] backdrop-blur-md border border-[#B79256]/30 text-[#2A2823] text-xs transition-colors"
              title="Upload a banner photo"
            >
              <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {bannerSrc ? "Change banner" : "Add a banner"}
            </button>
            {!isDefaultBanner && (
              <button
                type="button"
                data-testid="adjust-banner-btn"
                onClick={() => setBannerAdjusting((v) => !v)}
                disabled={bannerBusy}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-[#FBF8F1]/85 hover:bg-[#FBF8F1] backdrop-blur-md border border-[#B79256]/30 text-[#2A2823] text-xs transition-colors"
                title="Reposition and zoom your banner"
              >
                <Move className="w-3.5 h-3.5" strokeWidth={1.75} />
                {bannerAdjusting ? "Close adjuster" : "Adjust position"}
              </button>
            )}
            {!isDefaultBanner && (
              <button
                type="button"
                data-testid="remove-banner-btn"
                onClick={removeBanner}
                disabled={bannerBusy}
                className="inline-flex items-center rounded-full p-1.5 bg-[#FBF8F1]/85 hover:bg-[#FBF8F1] backdrop-blur-md border border-[#B24A2A]/40 text-[#B24A2A] transition-colors"
                title="Remove banner"
                aria-label="Remove banner"
              >
                <XIcon className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            )}
            <input
              ref={bannerInputRef}
              data-testid="banner-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickBanner}
            />
          </div>
        )}
      </div>

      {/* Pan + zoom adjuster — only rendered for the profile owner when
          they've explicitly opened it, and only for user-uploaded banners
          (the default Willoah banner is intentionally not adjustable). */}
      {isMe && bannerAdjusting && !isDefaultBanner && (
        <div className="mt-3" data-testid="banner-adjuster-wrap">
          <BannerAdjuster
            src={bannerSrc}
            initial={{
              banner_offset_x: bannerOffsetX,
              banner_offset_y: bannerOffsetY,
              banner_scale: bannerScale,
            }}
            saving={bannerBusy}
            onCancel={() => setBannerAdjusting(false)}
            onSave={saveBannerAdjust}
          />
        </div>
      )}

      {/* Header row: avatar + name + primary CTA.
          Explicit top margin (not a negative pull) so the banner has room
          to breathe before the Roots label. Avatar sits fully below the
          banner rather than half-overlapping it. */}
      <div className="mt-6 sm:mt-8 px-1 sm:px-6 flex flex-col md:flex-row md:items-end gap-4 sm:gap-6">
        <div className="relative shrink-0">
          <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 aspect-square rounded-full overflow-hidden bg-[#B79256]/15 border-4 border-[#FBF8F1] shadow-[0_6px_18px_rgb(42,40,35,0.08)] flex items-center justify-center">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={profile.name}
                data-testid="roots-avatar"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="font-serif-display text-4xl text-[#4E5A3D]">{profile.name?.[0]?.toUpperCase() || "·"}</span>
            )}
          </div>
          {isMe && (
            <>
              <button
                type="button"
                data-testid="change-photo-btn"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarBusy}
                className="absolute -bottom-1 -right-1 p-2 rounded-full bg-[#4E5A3D] text-[#FBF8F1] border-2 border-[#FBF8F1] hover:bg-[#3E4830] shadow-sm transition-colors"
                aria-label="Change profile photo"
                title="Change photo"
              >
                <Camera className="w-3.5 h-3.5" strokeWidth={2}/>
              </button>
              <input
                ref={avatarInputRef}
                data-testid="avatar-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickAvatar}
              />
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <span className="accent-label text-[#B79256]">roots</span>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            <h1 data-testid="roots-name" className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#2A2823] leading-none break-words">{profile.name}</h1>
            {activeThisMonth && (
              <span
                data-testid="active-pill"
                title="Planted a seed or added a Field Note in the last 30 days"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#4E5A3D]/12 border border-[#4E5A3D]/25 pl-2 pr-3 py-1 text-[11px]"
              >
                <Sparkles className="w-3 h-3 text-[#4E5A3D]" strokeWidth={2} />
                <span className="accent-label text-[#4E5A3D]">active this month</span>
              </span>
            )}
            {streakWeeks >= 4 && (
              <span
                data-testid="streak-badge"
                title={`${streakWeeks}-week honest-note streak — a Field Note in each of the last ${streakWeeks} weeks.`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#B79256] bg-[#B79256]/12 pl-2 pr-3 py-1 text-[#7A5A2E]"
              >
                <Sprout className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="accent-label text-[#7A5A2E]" data-testid="streak-badge-label">
                  {streakWeeks}-week streak
                </span>
              </span>
            )}
            {profile.is_founding_member && (
              <span
                data-testid="founding-member-badge"
                title="One of the first 100 people to plant a seed on Willoah"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#4E5A3D] bg-[#4E5A3D]/10 pl-2 pr-3 py-1 text-[#4E5A3D]"
              >
                <Star className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="accent-label text-[#4E5A3D]">founding member</span>
              </span>
            )}
          </div>

          {/* Tagline */}
          {profile.tagline && (
            <p data-testid="roots-tagline" className="mt-2 italic text-[#4E5A3D] text-base">
              {profile.tagline}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[#2A2823]/70">
            {isMe ? (
              <button
                type="button"
                data-testid="open-followers-btn"
                onClick={() => setFollowListMode("followers")}
                title="Only you can see who follows you"
                className="inline-flex items-baseline gap-1 -mx-1 px-1 rounded-md hover:bg-[#B79256]/12 transition-colors"
              >
                <b className="text-[#2A2823]">{followerCount}</b>
                <span className="accent-label text-[#B79256] ml-1">followers</span>
              </button>
            ) : (
              <span data-testid="follower-count"><b className="text-[#2A2823]">{followerCount}</b> <span className="accent-label text-[#B79256] ml-1">followers</span></span>
            )}
            {isMe ? (
              <button
                type="button"
                data-testid="open-following-btn"
                onClick={() => setFollowListMode("following")}
                title="Only you can see who you follow"
                className="inline-flex items-baseline gap-1 -mx-1 px-1 rounded-md hover:bg-[#B79256]/12 transition-colors"
              >
                <b className="text-[#2A2823]">{followingCount}</b>
                <span className="accent-label text-[#B79256] ml-1">following</span>
              </button>
            ) : (
              <span data-testid="following-count"><b className="text-[#2A2823]">{followingCount}</b> <span className="accent-label text-[#B79256] ml-1">following</span></span>
            )}
            <span data-testid="branch-count"><b className="text-[#2A2823]">{ideas.length}</b> <span className="accent-label text-[#B79256] ml-1">seeds</span></span>
          </div>
          <p className="mt-1 text-xs italic text-[#2A2823]/55">
            a seed is one honest thing {isMe ? "you've" : "they've"} shared — an outfit, recipe, place, or small thing that works
          </p>

          {/* Location (only shown to others when public) */}
          {profile.location && (
            <p data-testid="roots-location" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2A2823]/60">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
              {profile.location}
              {isMe && !profile.location_public && (
                <span
                  data-testid="location-private-hint"
                  className="inline-flex items-center gap-1 accent-label text-[10px] text-[#B79256] rounded-full bg-[#B79256]/12 px-2 py-0.5 ml-1"
                  title="Only you can see this — toggle 'Show my city' in Edit profile to make it public"
                >
                  <EyeOff className="w-2.5 h-2.5" strokeWidth={2} /> just you
                </span>
              )}
            </p>
          )}

          {/* Social handles — small link pills. Only rendered when the user
              has actually saved a handle so they never show as empty scaffolding. */}
          {(profile.instagram_handle || profile.facebook_url) && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="roots-socials">
              {profile.instagram_handle && (
                <a
                  href={instagramUrl(profile.instagram_handle)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="roots-instagram"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#B79256]/40 px-3 py-1 text-xs text-[#2A2823]/80 hover:text-[#4E5A3D] hover:border-[#4E5A3D]/50 hover:bg-[#4E5A3D]/5 transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span>@{profile.instagram_handle}</span>
                </a>
              )}
              {profile.facebook_url && (
                <a
                  href={facebookUrl(profile.facebook_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="roots-facebook"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#B79256]/40 px-3 py-1 text-xs text-[#2A2823]/80 hover:text-[#4E5A3D] hover:border-[#4E5A3D]/50 hover:bg-[#4E5A3D]/5 transition-colors"
                >
                  <Facebook className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span>Facebook</span>
                </a>
              )}
            </div>
          )}

          {profile.is_owner && (
            <span className="mt-3 inline-block accent-label px-3 py-1 rounded-full bg-[#4E5A3D]/10 text-[#4E5A3D]">site owner</span>
          )}
        </div>

        {/* Primary CTA */}
        <div className="shrink-0">
          {isMe ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="edit-profile-btn"
                onClick={() => setEditingProfile(true)}
                className="rounded-full h-11 px-6 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
              >
                <Pencil className="w-4 h-4 mr-2" strokeWidth={1.75}/>Edit profile
              </Button>
              <Link
                to="/gift-lists"
                data-testid="gift-lists-pill"
                title="Your private gift lists"
                className="inline-flex items-center gap-2 rounded-full h-11 px-5 border border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 transition-colors"
              >
                <Gift className="w-4 h-4 text-[#B79256]" strokeWidth={1.75} />
                <span className="accent-label text-[#2A2823]">Gift Lists</span>
              </Link>
              {profile.custom_picture_path && (
                <Button
                  data-testid="reset-photo-btn"
                  variant="outline"
                  onClick={resetAvatar}
                  disabled={avatarBusy}
                  className="rounded-full h-11 px-5 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]"
                  title="Use my Google photo again"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.75}/> Use Google photo
                </Button>
              )}
            </div>
          ) : (
            user && (
              <Button
                data-testid="follow-toggle-btn"
                onClick={toggleFollow}
                disabled={followBusy}
                className={`rounded-full h-11 px-7 text-base shadow-none ${isFollowing ? "bg-[#B79256]/15 text-[#2A2823] hover:bg-[#B79256]/25" : "bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830]"}`}
              >
                {isFollowing ? (
                  <><UserMinus className="w-4 h-4 mr-2" strokeWidth={1.75}/>Following</>
                ) : (
                  <><UserPlus className="w-4 h-4 mr-2" strokeWidth={1.75}/>Follow</>
                )}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Edit profile drawer */}
      {isMe && editingProfile && (
        <div
          data-testid="edit-profile-drawer"
          className="mt-8 rounded-2xl border border-[#B79256]/30 bg-white/70 p-6 space-y-5"
        >
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif-display text-2xl text-[#2A2823]">Edit profile</h2>
            <button
              type="button"
              data-testid="edit-profile-cancel"
              onClick={() => setEditingProfile(false)}
              className="text-[#2A2823]/50 hover:text-[#2A2823]"
              aria-label="Cancel"
            >
              <XIcon className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>

          <div>
            <label className="accent-label text-[#B79256]">Tagline</label>
            <p className="text-xs text-[#2A2823]/55 italic mt-1">One honest line about you — like "renovating our house since 2022".</p>
            <Input
              data-testid="tagline-input"
              value={form.tagline}
              onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value.slice(0, TAGLINE_MAX) }))}
              placeholder="e.g. Renovating our house since 2022"
              maxLength={TAGLINE_MAX}
              className="mt-2 rounded-full border-[#B79256]/40 bg-[#FBF8F1] h-10"
            />
            <p className="text-[10px] text-[#2A2823]/45 mt-1 text-right">{form.tagline.length}/{TAGLINE_MAX}</p>
          </div>

          <div>
            <label className="accent-label text-[#B79256]">City</label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                data-testid="location-input"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="Where you're rooted (e.g. Nashville, TN)"
                className="rounded-full max-w-xs border-[#B79256]/40 bg-[#FBF8F1] h-10"
              />
              <button
                type="button"
                data-testid="location-public-toggle"
                onClick={() => setForm((f) => ({ ...f, location_public: !f.location_public }))}
                className={`inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-xs transition-colors ${form.location_public ? "bg-[#4E5A3D]/12 border border-[#4E5A3D]/30 text-[#4E5A3D]" : "bg-[#B79256]/12 border border-[#B79256]/30 text-[#8B6E3F]"}`}
                title="Only visible on your profile when public"
              >
                {form.location_public ? (
                  <><Eye className="w-3.5 h-3.5" strokeWidth={2}/> visible on your profile</>
                ) : (
                  <><EyeOff className="w-3.5 h-3.5" strokeWidth={2}/> just for you</>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="accent-label text-[#B79256]">Banner focus</label>
            <p className="text-xs text-[#2A2823]/55 italic mt-1">Pick which part of your banner is centered.</p>
            <div className="mt-2 inline-flex rounded-full border border-[#B79256]/40 bg-[#FBF8F1] p-1">
              {["top", "center", "bottom"].map((pos) => (
                <button
                  type="button"
                  key={pos}
                  data-testid={`banner-focus-${pos}`}
                  onClick={() => setForm((f) => ({ ...f, banner_focus: pos }))}
                  className={`px-4 py-1.5 text-xs rounded-full capitalize transition-colors ${form.banner_focus === pos ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/70 hover:text-[#2A2823]"}`}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          {/* Instagram + Facebook — optional handles that surface as small
              pills on the profile. Accept either a bare handle or a full URL;
              backend normalises + validates. */}
          <div>
            <label className="accent-label text-[#B79256] flex items-center gap-1.5">
              <Instagram className="w-3.5 h-3.5" strokeWidth={1.75} /> Instagram
            </label>
            <p className="text-xs text-[#2A2823]/55 italic mt-1">Your handle only — no @ needed. Or paste your profile URL and we'll grab it.</p>
            <Input
              data-testid="instagram-input"
              value={form.instagram_handle}
              onChange={(e) => setForm((f) => ({ ...f, instagram_handle: e.target.value }))}
              placeholder="heywilloah"
              maxLength={60}
              className="mt-2 rounded-full border-[#B79256]/40 bg-[#FBF8F1] h-10"
            />
          </div>

          <div>
            <label className="accent-label text-[#B79256] flex items-center gap-1.5">
              <Facebook className="w-3.5 h-3.5" strokeWidth={1.75} /> Facebook
            </label>
            <p className="text-xs text-[#2A2823]/55 italic mt-1">Paste your Facebook URL or your page handle.</p>
            <Input
              data-testid="facebook-input"
              value={form.facebook_url}
              onChange={(e) => setForm((f) => ({ ...f, facebook_url: e.target.value }))}
              placeholder="facebook.com/your-page  or  your.handle"
              maxLength={500}
              className="mt-2 rounded-full border-[#B79256]/40 bg-[#FBF8F1] h-10"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              data-testid="edit-profile-save"
              onClick={saveProfile}
              disabled={savingForm}
              className="rounded-full h-10 px-5 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
            >
              <Check className="w-4 h-4 mr-2" strokeWidth={2}/> Save
            </Button>
          </div>
        </div>
      )}

      {profile.first_branch_at && (
        <div
          data-testid="milestone-first-branch"
          className="mt-10 rounded-2xl border border-[#4E5A3D]/30 bg-[#4E5A3D]/8 px-5 py-4 inline-flex items-baseline gap-3"
        >
          <span className="font-serif-display text-2xl leading-none text-[#4E5A3D]">🌱</span>
          <div>
            <div className="accent-label text-[#4E5A3D]">first seed planted</div>
            <div className="text-sm text-[#2A2823]/85 mt-0.5">
              {new Date(profile.first_branch_at).toLocaleDateString(undefined, {
                year: "numeric", month: "long", day: "numeric",
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-14">
        {isMe && (
          <div
            data-testid="roots-tabs"
            className="mb-6 flex items-center gap-1 border-b border-[#B79256]/25"
          >
            <button
              type="button"
              data-testid="tab-branches"
              onClick={() => goToTab("branches")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 transition-colors ${
                activeTab === "branches"
                  ? "border-[#4E5A3D] text-[#4E5A3D]"
                  : "border-transparent text-[#2A2823]/55 hover:text-[#2A2823]"
              }`}
            >
              <Sprout className="w-4 h-4" strokeWidth={1.75} />
              <span className="accent-label text-xs">Seeds · {ideas.length}</span>
            </button>
            <button
              type="button"
              data-testid="tab-saved"
              onClick={() => goToTab("saved")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 transition-colors ${
                activeTab === "saved"
                  ? "border-[#4E5A3D] text-[#4E5A3D]"
                  : "border-transparent text-[#2A2823]/55 hover:text-[#2A2823]"
              }`}
              title="Seeds you've bookmarked — only you can see this list"
            >
              <Bookmark className="w-4 h-4" strokeWidth={1.75} />
              <span className="accent-label text-xs">Saved</span>
            </button>
          </div>
        )}

        {activeTab === "saved" ? (
          <>
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <h2 className="font-serif-display text-3xl text-[#2A2823]">Seeds you've saved</h2>
                <p className="text-xs italic text-[#2A2823]/55 mt-1">
                  Only you can see this shelf. Tap a card to open it, tap the bookmark to unsave.
                </p>
              </div>
              {savedBranches.length > 0 && (
                <span className="accent-label text-[10px] text-[#2A2823]/55">{savedBranches.length} saved</span>
              )}
            </div>
            {savedLoading ? (
              <p className="mt-6 text-[#2A2823]/55 text-sm">Loading…</p>
            ) : savedBranches.length === 0 ? (
              <p data-testid="empty-saved" className="mt-6 text-[#2A2823]/60">
                Nothing saved yet. Tap the bookmark on any seed and it'll show up here.
              </p>
            ) : (
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {savedBranches.map((idea, i) => (
                  <IdeaCard
                    key={idea.id}
                    idea={idea}
                    index={i}
                    savedIds={savedIds}
                    onSaveChange={onSaveChange}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="font-serif-display text-3xl text-[#2A2823]">Seeds {isMe ? "you've" : "they've"} planted</h2>
              {sortedIdeas.length > 0 && (
                <span data-testid="branch-sort-label" className="accent-label text-[10px] text-[#2A2823]/55">
                  newest first · most recent {relativeShort(sortedIdeas[0].created_at)}
                </span>
              )}
            </div>
            {sortedIdeas.length === 0 ? (
              <p data-testid="empty-roots-ideas" className="mt-6 text-[#2A2823]/60">No seeds planted yet.</p>
            ) : (
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {sortedIdeas.map((idea, i) => (
                  <IdeaCard key={idea.id} idea={idea} index={i} savedIds={savedIds} onSaveChange={onSaveChange} />
                ))}
              </div>
            )}

            {/* Seeds this user has regrown. Only rendered when there's at
                least one, so the section doesn't clutter fresh profiles.
                Regrows never count toward the Seeds stat above — they live
                here in their own quiet subsection. */}
            {!regrowsLoading && regrows.length > 0 && (
              <div className="mt-14" data-testid="roots-regrows-section">
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <h2 className="font-serif-display text-3xl text-[#2A2823]">
                      Seeds {isMe ? "you've" : "they've"} regrown
                    </h2>
                    <p className="text-xs italic text-[#2A2823]/55 mt-1">
                      {isMe
                        ? "Good seeds you passed along. The original planter still gets the credit."
                        : "Good seeds they passed along. The original planter still gets the credit."}
                    </p>
                  </div>
                  <span className="accent-label text-[10px] text-[#2A2823]/55" data-testid="regrows-count-label">
                    {regrows.length} regrown
                  </span>
                </div>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regrows.map((idea, i) => (
                    <IdeaCard
                      key={idea.regrow_id || idea.id}
                      idea={idea}
                      index={i}
                      savedIds={savedIds}
                      onSaveChange={onSaveChange}
                      hideRegrowByline
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
    {isMe && followListMode && (
      <FollowListModal
        userId={profile.user_id}
        mode={followListMode}
        onClose={() => setFollowListMode(null)}
      />
    )}
    </>
  );
}

function relativeShort(iso) {
  if (!iso) return "";
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}
